import { FilterCondition, TFilterValue } from "@irohana-dev/components";
import { FC, useCallback, useMemo } from "react";
import { usePartnerFieldOptionsBatch } from "@/api/services/main/partner";
import { TSavedConditionShareScope } from "@/api/services/main/saved-condition/dtos/saved-condition.dto";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useFilterConditionPanel } from "@/components/commons/filter-condition/use-filter-condition-panel";
import { TFilterConditionPanelConfig } from "@/components/commons/filter-condition/types";
import {
  buildPartnerFilterFields,
  fromFilterValue,
  toFilterValue,
} from "@/features/partner/components/partner-list/partner-filter-condition-adapter";
import {
  PARTNER_LOCKED_COLUMN_IDS,
  TPartnerDisplayColumnSetting,
  TPartnerFilterFormExtended,
  defaultPartnerDisplayColumns,
  defaultPartnerFilterValues,
  normalizePartnerDateFields,
  normalizePartnerMultiSelectFields,
  stripEmptyPartnerFilterFields,
} from "@/features/partner/components/partner-list/partner-filter-shared";
import { usePartnerFilterMasterData } from "@/features/partner/components/partner-list/use-partner-filter-master-data";
import { PARTNER_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/partner/constants/common";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { FILTER_CONDITION_PANEL_WIDTH } from "@/components/commons/filter-condition/filter-condition-panel-width";

const panelConfig: TFilterConditionPanelConfig<TPartnerFilterFormExtended, TPartnerDisplayColumnSetting> = {
  pageType: "partner",
  defaultFilters: defaultPartnerFilterValues,
  defaultColumns: defaultPartnerDisplayColumns,
  columnsStorageKey: PARTNER_DISPLAY_COLUMNS_STORAGE_KEY,
  lockedColumnIds: PARTNER_LOCKED_COLUMN_IDS,
  normalizeFilters: (filters) => normalizePartnerMultiSelectFields(normalizePartnerDateFields(filters)),
  toFilterValue,
  fromFilterValue,
  stripEmptyFilters: stripEmptyPartnerFilterFields,
  getDateRange: (filters) => ({ from: filters.registrationDateFrom, to: filters.registrationDateTo }),
};

type Props = {
  isOpen: boolean;
  mode?: "create" | "edit";
  onClose: () => void;
  onApplyFilters?: (filters: TPartnerFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TPartnerDisplayColumnSetting[]) => void;
  initialFilters?: Partial<TPartnerFilterFormExtended>;
  initialDisplayColumns?: TPartnerDisplayColumnSetting[];
  appliedConditionId?: number | null;
  appliedConditionName?: string;
  appliedConditionShareScope?: TSavedConditionShareScope;
  appliedConditionIsDefault?: boolean;
  appliedConditionIncludeColumnSettings?: boolean;
  appliedConditionColumnSettings?: { order?: string[]; hidden?: string[] } | null;
  onConditionSaved?: (id: number, title: string) => void;
};

export const PartnerFilterConditionPanel: FC<Props> = ({ mode = "create", ...rest }) => {
  const { prefectureOptions, nationalityOptions, getMunicipalityNameOptionsByPrefectures } = usePartnerFilterMasterData();
  const fieldOptionsBatch = usePartnerFieldOptionsBatch();
  const panel = useFilterConditionPanel(panelConfig, { ...rest, mode });

  const prefectures = useMemo(() => (Array.isArray(panel.value.prefecture) ? panel.value.prefecture : []), [panel.value.prefecture]);

  const cityOptions = useMemo(
    () => (prefectures.length > 0 ? getMunicipalityNameOptionsByPrefectures(prefectures) : []),
    [prefectures, getMunicipalityNameOptionsByPrefectures],
  );

  const staticOptions = useMemo(() => ({ nationality: nationalityOptions, prefecture: prefectureOptions }), [nationalityOptions, prefectureOptions]);

  const fields = useMemo(
    () => buildPartnerFilterFields(staticOptions, cityOptions, prefectures.length > 0, panel.value.isOverseas === true, fieldOptionsBatch),
    [staticOptions, cityOptions, prefectures.length, panel.value.isOverseas, fieldOptionsBatch],
  );

  const handleChange = useCallback(
    (next: TFilterValue) => {
      panel.setValue((prev) => {
        let result = next;
        if (prev.isOverseas === true && next.isOverseas !== true) {
          const { location: _location, ...restValue } = result;
          result = restValue;
        }
        const prevPrefecture = Array.isArray(prev.prefecture) ? prev.prefecture : [];
        const nextPrefecture = Array.isArray(result.prefecture) ? result.prefecture : [];
        if (JSON.stringify(prevPrefecture) === JSON.stringify(nextPrefecture)) return result;
        const { city: _city, ...restValue } = result;
        return restValue;
      });
    },
    [panel.setValue],
  );

  return (
    <>
      <FilterCondition
        width={FILTER_CONDITION_PANEL_WIDTH}
        shareColor={filterConditionShareColor}
        isOpen={rest.isOpen}
        mode={panel.displayMode}
        onClose={rest.onClose}
        fields={fields}
        value={panel.value}
        onChange={handleChange}
        columns={panel.columnOptions}
        columnOrder={panel.columnOrder}
        hiddenColumns={panel.hiddenColumns}
        lockedColumns={PARTNER_LOCKED_COLUMN_IDS}
        onColumnSettingsChange={panel.handleColumnSettingsChange}
        conditionName={panel.conditionName}
        onConditionNameChange={panel.handleConditionNameChange}
        conditionNameError={panel.conditionNameError}
        appliedConditionId={rest.appliedConditionId ?? undefined}
        shareScope={panel.shareScope}
        onShareScopeChange={panel.setShareScope}
        isDefault={panel.isDefault}
        onIsDefaultChange={panel.setIsDefault}
        includeColumnSettings={panel.includeColumnSettings}
        onIncludeColumnSettingsChange={panel.setIncludeColumnSettings}
        hasSavedColumnSettings={panel.hasSavedColumnSettings}
        onSaveCondition={panel.handleSaveCondition}
        isSaving={panel.isSaving}
        onApply={panel.handleApply}
        onClear={() => panel.hasDataToClear && panel.setIsClearConfirmOpen(true)}
      />
      <ConfirmDeleteModal
        open={panel.isClearConfirmOpen}
        onClose={() => panel.setIsClearConfirmOpen(false)}
        onOk={panel.clearValue}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
